// To / Cc field for the email composers. Plain comma-separated text underneath — the
// same string the send routes already take — with a dropdown of addresses the app
// knows: the people on this job or bid first (owner, GC, billing contact, bid
// recipients, the PM), then the client directory, then Titan's own people. Nothing is
// read from anyone's computer or phone. You can always just type an address.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../services/apiClient';

// One fetch per job/bid per page load; every composer on the page shares it.
const cache = new Map();
function loadBook(projectId, bidId) {
  const key = `${projectId || ''}|${bidId || ''}`;
  if (!cache.has(key)) {
    const q = [projectId ? `projectId=${encodeURIComponent(projectId)}` : '', bidId ? `bidId=${encodeURIComponent(bidId)}` : ''].filter(Boolean).join('&');
    cache.set(key, apiFetch(`/mail/address-book${q ? '?' + q : ''}`).catch(() => ({ job: [], clients: [], employees: [] })));
  }
  return cache.get(key);
}

const GROUPS = [['job', 'On this job'], ['clients', 'Client directory'], ['employees', 'Titan']];

export default function AddressPicker({ value, onChange, placeholder, projectId, bidId, extra = [], style, inputStyle, autoFocus }) {
  const [book, setBook] = useState(null);
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const wrap = useRef(null);
  useEffect(() => { let live = true; loadBook(projectId, bidId).then(b => { if (live) setBook(b); }); return () => { live = false; }; }, [projectId, bidId]);
  useEffect(() => {
    function onDoc(e) { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // The address being typed is whatever follows the last comma.
  const text = String(value || '');
  const cut = text.lastIndexOf(',');
  const head = cut >= 0 ? text.slice(0, cut + 1) : '';
  const token = (cut >= 0 ? text.slice(cut + 1) : text).trim().toLowerCase();
  const chosen = new Set(text.split(',').map(s => s.trim().toLowerCase()).filter(Boolean));

  const options = useMemo(() => {
    if (!book) return [];
    const all = [
      ...extra.map(x => ({ ...x, group: 'job' })),
      ...GROUPS.flatMap(([g]) => (book[g] || []).map(x => ({ ...x, group: g }))),
    ];
    const seen = new Set();
    const out = [];
    for (const o of all) {
      const e = String(o.email || '').toLowerCase();
      if (!e || seen.has(e) || chosen.has(e)) continue;
      if (token && !`${o.name || ''} ${o.email} ${o.note || ''}`.toLowerCase().includes(token)) continue;
      seen.add(e); out.push(o);
    }
    // With nothing typed, just the people on the job — the directory would be 150 rows.
    return token ? out.slice(0, 40) : out.filter(o => o.group === 'job');
  }, [book, extra, token, chosen]); // eslint-disable-line react-hooks/exhaustive-deps

  function pick(o) {
    const next = `${head}${head ? ' ' : ''}${o.email}, `;
    onChange(next);
    setHi(0);
  }
  function onKeyDown(e) {
    if (!open || !options.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi(h => Math.min(h + 1, options.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter' && token) { e.preventDefault(); pick(options[hi] || options[0]); }
    else if (e.key === 'Escape') setOpen(false);
  }

  const showDrop = open && (options.length > 0 || token.length > 0);
  let lastGroup = null;
  return (
    <div ref={wrap} style={{ position: 'relative', ...(style || {}) }}>
      <input value={text} autoFocus={autoFocus}
        onChange={e => { onChange(e.target.value); setOpen(true); setHi(0); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        style={inputStyle} />
      {showDrop && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 60, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, marginTop: 2, maxHeight: 260, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.14)' }}>
          {options.length === 0 && (
            <div style={{ padding: '8px 10px', fontSize: 12, color: '#9ca3af' }}>
              {token.includes('@') ? 'Not in the app’s address book — that’s fine, it’ll send to what you typed.' : 'No match — keep typing an address, or a name from the client directory or Titan.'}
            </div>
          )}
          {options.map((o, i) => {
            const header = o.group !== lastGroup ? (GROUPS.find(g => g[0] === o.group) || [])[1] : null;
            lastGroup = o.group;
            return (
              <React.Fragment key={o.email + i}>
                {header && <div style={{ padding: '6px 10px 2px', fontSize: 10, fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4 }}>{header}</div>}
                <div onMouseDown={e => { e.preventDefault(); pick(o); }} onMouseEnter={() => setHi(i)}
                  style={{ padding: '7px 10px', cursor: 'pointer', background: i === hi ? '#f3f4f6' : '#fff', display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#111827', whiteSpace: 'nowrap' }}>{o.name}</span>
                  <span style={{ fontSize: 12, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.email}</span>
                  {o.note && <span style={{ marginLeft: 'auto', fontSize: 10.5, color: '#9ca3af', whiteSpace: 'nowrap' }}>{o.note}</span>}
                </div>
              </React.Fragment>
            );
          })}
          {!token && options.length > 0 && <div style={{ padding: '6px 10px 8px', fontSize: 11, color: '#9ca3af', borderTop: '1px solid #f3f4f6' }}>Type a name to search the client directory and Titan’s people.</div>}
        </div>
      )}
    </div>
  );
}
